import { CREDIT_PRODUCT_TYPE_CODES } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/constants.ts';
import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import {
  BorrowerType,
  FinancialStatementForm,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import type {
  TFinancialConditionFormValues,
  TFinancialConditionCertificateDto,
  TLoanOrBond,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import {
  formatCertificateDate,
  getCertificateBalanceMonthNames,
  isMonthlyReportPeriod,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/utils.ts';
import type {
  TDocumentGenerationBank,
  TDocumentGenerationCurrency,
} from '~/store/covenants/types.ts';

type TBankNameVariant = 'full' | 'short';
const isFilled = (value: unknown): boolean => {
  if (value instanceof Date) return true;
  if (typeof value === 'string') return Boolean(value.trim());
  if (value && typeof value === 'object' && 'label' in value) {
    return Boolean((value as TAutocompleteOption).label.trim());
  }

  return false;
};

const hasFilledValue = (...values: unknown[]) => values.some(isFilled);

const hasLiabilityContent = (row: TFinancialConditionFormValues['liabilitiesAnnual'][number]) =>
  hasFilledValue(
    row.creditor,
    row.liabilityType,
    row.amount,
    row.purpose,
    row.originDate,
    row.repaymentDate
  );

const hasExpenseContent = (row: TFinancialConditionFormValues['expensesAnnual'][number]) =>
  hasFilledValue(row.component, row.amount, row.sharePercent);

const toTrimmedString = (value: string): string | undefined => value.trim() || undefined;

const toNumber = (value: string): number | undefined => {
  if (!value.trim()) return undefined;

  const parsedValue = Number(value.replace(',', '.'));

  return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

const resolveBank = (
  option: TAutocompleteOption | null,
  banks: TDocumentGenerationBank[],
  variant: TBankNameVariant
) => {
  if (!option) {
    return { name: undefined, code: null };
  }

  const bank = banks.find((item) => String(item.id) === option.value);
  const name =
    variant === 'short'
      ? bank?.shortname ?? bank?.sname ?? bank?.name ?? bank?.fullName
      : bank?.fullName ?? bank?.name ?? bank?.shortname ?? bank?.sname;

  return {
    name: name ?? option.label,
    code: bank?.idNbu === null || bank?.idNbu === undefined ? null : String(bank.idNbu),
  };
};

const getCurrencyCode = (
  option: TAutocompleteOption | null,
  currencies: TDocumentGenerationCurrency[]
): string | undefined => {
  if (!option) return undefined;

  const currency = currencies.find((item) => String(item.id) === option.value);

  return currency?.symbolCode?.trim() || undefined;
};

const getReportLine = (value: string): string | undefined => {
  const reportLine = value.replace(/\D/g, '');

  return reportLine || undefined;
};

const getCreditProductCode = (option: TAutocompleteOption | null): string | undefined =>
  option ? CREDIT_PRODUCT_TYPE_CODES[option.value] : undefined;

const mapLoans = (
  loans: TLoanOrBond[],
  indicator: 'Позики' | 'Облігаційні позики',
  banks: TDocumentGenerationBank[],
  currencies: TDocumentGenerationCurrency[]
) =>
  loans
    .filter((loan) =>
      hasFilledValue(
        loan.lenderName,
        loan.lenderCode,
        loan.receivedDate,
        loan.maturityDate,
        loan.currency,
        loan.currentAmount,
        loan.balanceLine,
        loan.overdueDebt
      )
    )
    .map((loan) => ({
      indicator,
      creditorName: resolveBank(loan.lenderName, banks, 'short').name,
      creditorEdrpou: toTrimmedString(loan.lenderCode),
      receiveDate: formatCertificateDate(loan.receivedDate),
      repaymentDate: formatCertificateDate(loan.maturityDate),
      currency: getCurrencyCode(loan.currency, currencies),
      currentAmount: toNumber(loan.currentAmount),
      balanceLine: getReportLine(loan.balanceLine),
      overdueAmount: toNumber(loan.overdueDebt),
    }));

export const serializeFinancialConditionCertificate = (
  values: TFinancialConditionFormValues,
  banks: TDocumentGenerationBank[],
  currencies: TDocumentGenerationCurrency[]
): TFinancialConditionCertificateDto => {
  const [balanceMonth1, balanceMonth2, balanceMonth3] = getCertificateBalanceMonthNames(
    values.reportPeriod,
    values.reportYear
  );
  const reportDate = formatCertificateDate(values.reportDate) ?? '';
  const reportPeriod = `${values.reportPeriod?.label ?? ''} ${values.reportYear} року`.trim();
  const isMonthlyPeriod = isMonthlyReportPeriod(values.reportPeriod);
  const borrowerType = values.borrowerType?.value;
  const isLegalEntity = borrowerType === BorrowerType.LegalEntity;
  const isSoleTrader = borrowerType === BorrowerType.SoleTrader;
  const financialStatementForm = values.financialStatementForm?.value as
    | FinancialStatementForm
    | undefined;
  const shouldIncludeCashInflows =
    isSoleTrader || values.hasCashFlowTransferRequirement;
  const additionalInfo = values.additionalInfo?.trim();

  return {
    ...(isLegalEntity && financialStatementForm
      ? {
          isFullFormReporting: financialStatementForm === FinancialStatementForm.Large,
        }
      : {}),
    documentDate: formatCertificateDate(new Date()) ?? '',
    borrowerFullName: values.borrowerName?.trim() ?? '',
    borrowerEdrpou: values.borrowerCode?.trim() ?? '',
    reportPeriod,
    reportDate,
    balanceMonth1,
    balanceMonth2,
    balanceMonth3,
    ...(additionalInfo ? { additionalInfo } : {}),
    cashInflows: shouldIncludeCashInflows
      ? values.incomeBanks
          .filter((row) => hasFilledValue(row.bankId, row.currency, row.total, row.salesRevenue))
          .map((row) => {
            const bank = resolveBank(row.bankId, banks, 'full');

            return {
              bankName: bank.name,
              bankCode: bank.code,
              currency: getCurrencyCode(row.currency, currencies),
              totalAmount: toNumber(row.total),
              revenueAmount: toNumber(row.salesRevenue),
            };
          })
      : [],
    averageBalances: values.hasAverageBalanceRequirement
      ? values.averageBalanceBanks
          .filter((row) =>
            hasFilledValue(
              row.bankId,
              row.currency,
              row.firstMonthAmount,
              row.secondMonthAmount,
              row.thirdMonthAmount
            )
          )
          .map((row) => {
            const bank = resolveBank(row.bankId, banks, 'full');

            return {
              bankName: bank.name,
              bankCode: bank.code,
              currency: getCurrencyCode(row.currency, currencies),
              month1Amount: toNumber(row.firstMonthAmount),
              ...(isMonthlyPeriod
                ? {}
                : {
                    month2Amount: toNumber(row.secondMonthAmount),
                    month3Amount: toNumber(row.thirdMonthAmount),
                  }),
            };
          })
      : [],
    creditOperations: values.hasNoCreditOperations
      ? []
      : values.creditOperations
          .filter((row) =>
            hasFilledValue(
              row.lenderName,
              row.productType,
              row.currency,
              row.limit,
              row.outstandingDebt,
              row.interestRate,
              row.issueDate,
              row.maturityDate,
              row.overdueDebt
            )
          )
          .map((row) => ({
            creditorName: resolveBank(row.lenderName, banks, 'short').name,
            productType: getCreditProductCode(row.productType),
            currency: getCurrencyCode(row.currency, currencies),
            limitAmount: toNumber(row.limit),
            debtAmount: toNumber(row.outstandingDebt),
            interestRate: toNumber(row.interestRate),
            issueDate: formatCertificateDate(row.issueDate),
            maturityDate: formatCertificateDate(row.maturityDate),
            overdueAmount: toNumber(row.overdueDebt),
          })),
    financialAidGiven: values.providedFinancialAssistance
      .filter((row) =>
        hasFilledValue(
          row.counterpartyName,
          row.currency,
          row.outstandingDebt,
          row.issueOrReceiptDate,
          row.maturityDate,
          row.balanceLine,
          row.overdueDebt
        )
      )
      .map((row) => ({
        counterpartyName: toTrimmedString(row.counterpartyName),
        currency: getCurrencyCode(row.currency, currencies),
        debtAmount: toNumber(row.outstandingDebt),
        issueDate: formatCertificateDate(row.issueOrReceiptDate),
        repaymentDate: formatCertificateDate(row.maturityDate),
        balanceLine: getReportLine(row.balanceLine),
        overdueAmount: toNumber(row.overdueDebt),
      })),
    financialAidReceived: values.receivedFinancialAssistance
      .filter((row) =>
        hasFilledValue(
          row.counterpartyName,
          row.currency,
          row.outstandingDebt,
          row.issueOrReceiptDate,
          row.maturityDate,
          row.balanceLine,
          row.overdueDebt
        )
      )
      .map((row) => ({
        counterpartyName: toTrimmedString(row.counterpartyName),
        currency: getCurrencyCode(row.currency, currencies),
        debtAmount: toNumber(row.outstandingDebt),
        issueDate: formatCertificateDate(row.issueOrReceiptDate),
        repaymentDate: formatCertificateDate(row.maturityDate),
        balanceLine: getReportLine(row.balanceLine),
        overdueAmount: toNumber(row.overdueDebt),
      })),
    loans: values.hasNoLoansAndBonds
      ? []
      : [
          ...mapLoans(values.receivedLoans, 'Позики', banks, currencies),
          ...mapLoans(values.bondLoans, 'Облігаційні позики', banks, currencies),
        ],
    offBalanceLiabilities: values.offBalanceLiabilities
      .filter((row) =>
        hasFilledValue(
          row.debtor,
          row.creditor,
          row.obligationType,
          row.currency,
          row.limitAmount,
          row.actualAmount,
          row.interestRate,
          row.openDate,
          row.endDate
        )
      )
      .map((row) => ({
        debtor: toTrimmedString(row.debtor),
        creditor: resolveBank(row.creditor, banks, 'short').name,
        obligationType: toTrimmedString(row.obligationType),
        currency: getCurrencyCode(row.currency, currencies),
        limitAmount: toNumber(row.limitAmount),
        actualAmount: toNumber(row.actualAmount),
        interestRate: toNumber(row.interestRate),
        openDate: formatCertificateDate(row.openDate),
        endDate: formatCertificateDate(row.endDate),
      })),
    currencyExchanges: values.currencyExchanges
      .filter((row) =>
        hasFilledValue(row.bank, row.soldCurrency, row.soldAmount, row.boughtCurrency, row.boughtAmount)
      )
      .map((row) => {
        const bank = resolveBank(row.bank, banks, 'full');

        return {
          bankName: bank.name,
          bankCode: bank.code,
          soldCurrency: getCurrencyCode(row.soldCurrency, currencies),
          soldAmount: toNumber(row.soldAmount),
          boughtCurrency: getCurrencyCode(row.boughtCurrency, currencies),
          boughtAmount: toNumber(row.boughtAmount),
        };
      }),
    ...(isSoleTrader
      ? {
          incomeBreakdowns: values.incomeBreakdowns
          .filter((row) =>
            hasFilledValue(
              row.counterpartyName,
              row.counterpartyEdrpou,
              row.reportLine,
              row.incomeType,
              row.amount,
              row.sharePercent
            )
          )
          .map((row) => ({
            counterpartyName: toTrimmedString(row.counterpartyName),
            counterpartyEdrpou: toTrimmedString(row.counterpartyEdrpou),
            reportLine: getReportLine(row.reportLine),
            component: toTrimmedString(row.incomeType),
            amount: toNumber(row.amount),
            sharePercent: toNumber(row.sharePercent),
          })),
        }
      : {}),
    liabilitiesAnnual: isLegalEntity
      ? values.liabilitiesAnnual
          .filter(hasLiabilityContent)
          .map((row) => ({
            creditor: resolveBank(row.creditor, banks, 'short').name,
            liabilityType: toTrimmedString(row.liabilityType),
            amount: toNumber(row.amount),
            purpose: toTrimmedString(row.purpose),
            originDate: formatCertificateDate(row.originDate),
            repaymentDate: formatCertificateDate(row.repaymentDate),
            reportLine: getReportLine(row.reportLine),
          }))
      : [],
    liabilitiesQuarterly: isLegalEntity
      ? values.liabilitiesQuarterly
          .filter(hasLiabilityContent)
          .map((row) => ({
            creditor: resolveBank(row.creditor, banks, 'short').name,
            liabilityType: toTrimmedString(row.liabilityType),
            amount: toNumber(row.amount),
            purpose: toTrimmedString(row.purpose),
            originDate: formatCertificateDate(row.originDate),
            repaymentDate: formatCertificateDate(row.repaymentDate),
            reportLine: getReportLine(row.reportLine),
          }))
      : [],
    financialInvestments: isLegalEntity
      ? values.financialInvestments
          .filter((row) =>
            hasFilledValue(row.name, row.edrpou, row.sharePercent, row.amount, row.startDate)
          )
          .map((row) => ({
            name: toTrimmedString(row.name),
            edrpou: toTrimmedString(row.edrpou),
            sharePercent: toNumber(row.sharePercent),
            amount: toNumber(row.amount),
            startDate: formatCertificateDate(row.startDate),
          }))
      : [],
    receivables: isLegalEntity
      ? values.receivables
          .filter((row) =>
            hasFilledValue(
              row.counterpartyName,
              row.edrpou,
              row.subject,
              row.totalDebt,
              row.overdueAmount,
              row.originDate,
              row.repaymentDate
            )
          )
          .map((row) => ({
            counterpartyName: toTrimmedString(row.counterpartyName),
            edrpou: toTrimmedString(row.edrpou),
            subject: toTrimmedString(row.subject),
            totalDebt: toNumber(row.totalDebt),
            overdueAmount: toNumber(row.overdueAmount),
            originDate: formatCertificateDate(row.originDate),
            repaymentDate: formatCertificateDate(row.repaymentDate),
          }))
      : [],
    expensesAnnual: isLegalEntity
      ? values.expensesAnnual
          .filter(hasExpenseContent)
          .map((row) => ({
            reportLine: getReportLine(row.reportLine),
            component: toTrimmedString(row.component),
            amount: toNumber(row.amount),
            sharePercent: toNumber(row.sharePercent),
          }))
      : [],
    expensesQuarterly: isLegalEntity
      ? values.expensesQuarterly
          .filter(hasExpenseContent)
          .map((row) => ({
            reportLine: getReportLine(row.reportLine),
            component: toTrimmedString(row.component),
            amount: toNumber(row.amount),
            sharePercent: toNumber(row.sharePercent),
          }))
      : [],
    expensesQuarterlySmall: isLegalEntity
      ? values.expensesQuarterlySmall
          .filter(hasExpenseContent)
          .map((row) => ({
            reportLine: getReportLine(row.reportLine),
            component: toTrimmedString(row.component),
            amount: toNumber(row.amount),
            sharePercent: toNumber(row.sharePercent),
          }))
      : [],
    budgetDebts: values.budgetDebts
      .filter((row) => hasFilledValue(row.debtType, row.amount))
      .map((row) => ({
        debtType: toTrimmedString(row.debtType),
        amount: toNumber(row.amount),
      })),
    courtCases: values.courtCases
      .filter((row) =>
        hasFilledValue(
          row.otherParty,
          row.borrowerName,
          row.borrowerStatus,
          row.subject,
          row.claimAmount,
          row.claimDate,
          row.currentState
        )
      )
      .map((row) => ({
        otherParty: toTrimmedString(row.otherParty),
        borrowerName: toTrimmedString(row.borrowerName),
        borrowerStatus: toTrimmedString(row.borrowerStatus),
        subject: toTrimmedString(row.subject),
        claimAmount: toNumber(row.claimAmount),
        claimDate: formatCertificateDate(row.claimDate),
        currentState: toTrimmedString(row.currentState),
      })),
  };
};
